import React, { useState } from 'react';
import { X, User, Mail, Lock, Phone, GraduationCap, Target } from 'lucide-react';
import { apiService } from '../../services/api';
import ProfileImageUpload from '../common/ProfileImageUpload';
import '../../styles/create-mentor-modal.scss';

interface CreateMenteeModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface MenteeFormData {
  // User info
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
  phone?: string;
  bio?: string;
  profile_image?: File | null;

  // Mentee specific
  current_position?: string;
  career_goals?: string;
  learning_preferences?: string;
}

const CreateMenteeModal: React.FC<CreateMenteeModalProps> = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<MenteeFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    bio: '',
    profile_image: null,
    current_position: '',
    career_goals: '',
    learning_preferences: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageSelected = (file: File) => {
    setFormData(prev => ({ ...prev, profile_image: file }));
  };

  const handleImageRemoved = () => {
    setFormData(prev => ({ ...prev, profile_image: null }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) newErrors.email = 'El email es requerido';
    if (!formData.password) newErrors.password = 'La contraseña es requerida';
    else if (formData.password.length < 6) newErrors.password = 'Mínimo 6 caracteres';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }
    if (!formData.first_name) newErrors.first_name = 'El nombre es requerido';
    if (!formData.last_name) newErrors.last_name = 'El apellido es requerido';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const submitData = new FormData();
      submitData.append('email', formData.email);
      submitData.append('password', formData.password);
      submitData.append('first_name', formData.first_name);
      submitData.append('last_name', formData.last_name);
      submitData.append('role_name', 'mentee');

      if (formData.phone) submitData.append('phone', formData.phone);
      if (formData.bio) submitData.append('bio', formData.bio);
      if (formData.current_position) submitData.append('current_position', formData.current_position);
      if (formData.career_goals) submitData.append('career_goals', formData.career_goals);
      if (formData.learning_preferences) submitData.append('learning_preferences', formData.learning_preferences);

      if (formData.profile_image) {
        submitData.append('profile_image', formData.profile_image);
      }

      await apiService.post('/admin/mentees', submitData);

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating mentee:', error);
      setErrors({
        submit: error.response?.data?.message || 'Error al crear mentee. Intenta nuevamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-mentor-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <div className="modal-header__content">
            <GraduationCap size={24} className="header-icon" />
            <div>
              <h2>Crear Nuevo Mentee</h2>
              <p className="modal-subtitle">Agregar un nuevo aprendiz al sistema</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {errors.submit && (
              <div className="alert alert--danger">
                {errors.submit}
              </div>
            )}

            {/* Profile Image Upload */}
            <div className="form-section">
              <h3 className="form-section-title">Foto de Perfil</h3>
              <ProfileImageUpload
                onImageSelected={handleImageSelected}
                onImageRemoved={handleImageRemoved}
                size="lg"
                maxSize={5}
              />
            </div>

            {/* Información Personal */}
            <div className="form-section">
              <h3 className="form-section-title">
                <User size={18} />
                Información Personal
              </h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Nombre</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={errors.first_name ? 'error' : ''}
                    placeholder="Juan"
                  />
                  {errors.first_name && <span className="error-message">{errors.first_name}</span>}
                </div>

                <div className="form-group">
                  <label>Apellido</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={errors.last_name ? 'error' : ''}
                    placeholder="Pérez"
                  />
                  {errors.last_name && <span className="error-message">{errors.last_name}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <Mail size={16} />
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="juan.perez@email.com"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label>
                    <Phone size={16} />
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+51 999 888 777"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Biografía</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Cuéntanos sobre ti..."
                />
              </div>
            </div>

            {/* Credenciales de Acceso */}
            <div className="form-section">
              <h3 className="form-section-title">
                <Lock size={18} />
                Credenciales de Acceso
              </h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Contraseña</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={errors.password ? 'error' : ''}
                    placeholder="Mínimo 8 caracteres"
                  />
                  {errors.password && <span className="error-message">{errors.password}</span>}
                </div>

                <div className="form-group">
                  <label>Confirmar Contraseña</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={errors.confirmPassword ? 'error' : ''}
                    placeholder="Repite la contraseña"
                  />
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                </div>
              </div>
            </div>

            {/* Información Profesional */}
            <div className="form-section">
              <h3 className="form-section-title">
                <Target size={18} />
                Información de Carrera
              </h3>

              <div className="form-group">
                <label>Posición Actual</label>
                <input
                  type="text"
                  name="current_position"
                  value={formData.current_position}
                  onChange={handleInputChange}
                  placeholder="ej. Desarrollador Junior"
                />
              </div>

              <div className="form-group">
                <label>Objetivos de Carrera</label>
                <textarea
                  name="career_goals"
                  value={formData.career_goals}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="¿Qué esperas lograr con la mentoría?"
                />
              </div>

              <div className="form-group">
                <label>Preferencias de Aprendizaje</label>
                <textarea
                  name="learning_preferences"
                  value={formData.learning_preferences}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="¿Cómo prefieres aprender? (ej. visual, práctico, teórico)"
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn--secondary" onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary" disabled={loading}>
              {loading ? 'Creando...' : 'Crear Mentee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMenteeModal;
